"use client";

import * as z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PermutaService from "@/components/PermutaService";

const permutaSchema = z.object({
    targa: z.string().max(7, { message: "La targa deve essere composta da 7 caratteri" }).regex(/^[A-Z0-9]{7}$/, { message: "La targa deve essere composta da lettere maiuscole e numeri" }),
    marca: z.string().min(1, { message: "Inserisci la marca dell'auto" }),
    modello: z.string().min(1, { message: "Inserisci il modello dell'auto" }),
    versione: z.string().min(1, { message: "Inserisci la versione dell'auto" }),
    meseImmatricolazione: z.string().min(1, { message: "Inserisci il mese di immatricolazione dell'auto" }),
    annoImmatricolazione: z.string().min(1, { message: "Inserisci l'anno di immatricolazione dell'auto" }),
    km: z.string().min(1, { message: "Inserisci i km dell'auto" }),
    valoreDesiderato: z.string().min(1, { message: "Inserisci il valore desiderato dell'auto" }),
});

const FormSchema = z.object({
    option: z.string().min(1, { message: "Devi selezionare un'opzione" }),
});

interface PermutaLegacyProps {
    onSubmit: (data: any) => void;
    goBack: () => void;
    onChange?: (data: { option: string }) => void;
}

const PermutaLegacy = ({ onSubmit, goBack, onChange }: PermutaLegacyProps) => {
    const [anni, setAnni] = useState<string[]>([]);
    const [mesi, setMesi] = useState<{ value: string; label: string }[]>([]);
    const [modelli, setModelli] = useState<any[]>([]);
    const [versioni, setVersioni] = useState<any[]>([]);
    const [marche, setMarche] = useState<any[]>([]);

    const permutaForm = useForm<z.infer<typeof permutaSchema>>({
        resolver: zodResolver(permutaSchema),
        mode: "onTouched",
        defaultValues: {
            targa: "FC042DM",
            marca: "FIA",
            modello: "0435",
            versione: "A011629",
            meseImmatricolazione: "1",
            annoImmatricolazione: "2015",
            km: "192000",
            valoreDesiderato: "4000",
        },
    });

    const form = useForm<z.infer<typeof FormSchema>>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            option: "rottamazione",
        },
    });

    const [isPermutaSelected, setPermutaSelected] = useState(false);

    useEffect(() => {
        onChange?.({ option: form.getValues("option") });
    }, [form, onChange]);

    function handleSubmit(data: z.infer<typeof FormSchema>) {
        if (data.option === "permuta") {
            setPermutaSelected(true);
        } else {
            onSubmit(data);
        }
    }

    function onSubmitPermuta(data: z.infer<typeof permutaSchema>) {
        setPermutaSelected(false);
        onSubmit({
            ...data,
            option: "permuta",
        });
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/makes`);
                if (!response.ok) {
                    throw new Error("Errore nella risposta del server");
                }
                const data = await response.json();
                setMarche(data.marche);
                const currentValues = permutaForm.getValues();
                permutaForm.setValue("marca", currentValues.marca || "");
                permutaForm.setValue("modello", currentValues.modello || "");
                permutaForm.setValue("versione", currentValues.versione || "");
                permutaForm.setValue("meseImmatricolazione", currentValues.meseImmatricolazione || "");
                permutaForm.setValue("annoImmatricolazione", currentValues.annoImmatricolazione || "");
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [permutaForm]);

    useEffect(() => {
        const initializeForm = async () => {
            const { marca, annoImmatricolazione, meseImmatricolazione, modello, versione } = permutaForm.getValues();

            if (marca && !anni.length) {
                await fetchYears(marca);
                permutaForm.setValue("annoImmatricolazione", annoImmatricolazione);
                permutaForm.setValue("meseImmatricolazione", meseImmatricolazione);
            }

            if (annoImmatricolazione && !modelli.length) {
                await fetchModels(marca, annoImmatricolazione);
                permutaForm.setValue("modello", modello);
            }

            if (modello && !versioni.length) {
                await fetchVersions(modello, annoImmatricolazione);
                permutaForm.setValue("versione", versione);
            }
        };

        if (isPermutaSelected) {
            void initializeForm();
        }
    }, [anni.length, isPermutaSelected, modelli.length, permutaForm, versioni.length]);

    const fetchYears = async (marca?: string) => {
        const selectedMarca = marca || permutaForm.watch("marca");
        if (!selectedMarca) return;

        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/years/${selectedMarca}`);
            if (!response.ok) {
                throw new Error("Errore nella risposta del server");
            }
            const data = await response.json();
            setAnni(data.anni.map(String));
            setMesi([
                { value: "1", label: "Gennaio" },
                { value: "2", label: "Febbraio" },
                { value: "3", label: "Marzo" },
                { value: "4", label: "Aprile" },
                { value: "5", label: "Maggio" },
                { value: "6", label: "Giugno" },
                { value: "7", label: "Luglio" },
                { value: "8", label: "Agosto" },
                { value: "9", label: "Settembre" },
                { value: "10", label: "Ottobre" },
                { value: "11", label: "Novembre" },
                { value: "12", label: "Dicembre" },
            ]);
            setModelli([]);
            setVersioni([]);
        } catch (error) {
            console.error("Error fetching years:", error);
        }
    };

    const fetchModels = async (marca?: string, annoImmatricolazione?: string) => {
        const selectedMarca = marca || permutaForm.watch("marca");
        const selectedAnno = annoImmatricolazione || permutaForm.watch("annoImmatricolazione");
        if (!selectedMarca || !selectedAnno) return;

        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/models/${selectedMarca}/${selectedAnno}`);
            if (!response.ok) {
                throw new Error("Errore nella risposta del server");
            }
            const data = await response.json();
            setModelli(data.modelli);
            setVersioni([]);
        } catch (error) {
            console.error("Error fetching models:", error);
        }
    };

    const fetchVersions = async (modello?: string, annoImmatricolazione?: string) => {
        const selectedModello = modello || permutaForm.watch("modello");
        const selectedAnno = annoImmatricolazione || permutaForm.watch("annoImmatricolazione");
        if (!selectedModello || !selectedAnno) return;

        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/versions/${selectedModello}/${selectedAnno}`);
            if (!response.ok) {
                throw new Error("Errore nella risposta del server");
            }
            const data = await response.json();
            setVersioni(data.versioni);
            permutaForm.setValue("versione", "");
        } catch (error) {
            console.error("Error fetching versions:", error);
        }
    };

    useEffect(() => {
        void fetchYears();
    }, [permutaForm.watch("marca")]);

    useEffect(() => {
        void fetchModels();
    }, [permutaForm.watch("annoImmatricolazione")]);

    useEffect(() => {
        void fetchVersions();
    }, [permutaForm.watch("modello")]);

    const handleInputChange = (e: any) => {
        let value = e.target.value.toUpperCase();
        value = value.replace(/[^A-Z0-9]/g, "");

        if (value.length <= 2) {
            e.target.value = value;
        } else if (value.length <= 5) {
            e.target.value = `${value.slice(0, 2)}${value.slice(2, 5)}`;
        } else {
            e.target.value = `${value.slice(0, 2)}${value.slice(2, 5)}${value.slice(5, 7)}`;
        }
    };

    return (
        <div className="flex flex-col">
            <h1 className="text-2xl font-bold py-5">Vuoi Rottamare la tua auto usata?</h1>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8 h-[85%] flex flex-col justify-between">
                    <FormField
                        control={form.control}
                        name="option"
                        render={({ field }) => (
                            <FormItem>
                                <RadioGroup
                                    onValueChange={(value) => {
                                        field.onChange(value);
                                        onChange?.({ option: value });
                                    }}
                                    value={field.value}
                                    className="mt-4 rounded-2xl border gap-0 border-gray-200 bg-white shadow-sm divide-y divide-gray-200"
                                >
                                    <FormItem className={`flex items-start gap-4 px-6 py-5 transition ${field.value === "rottamazione" ? "bg-blue-50 rounded-t-2xl" : ""}`}>
                                        <FormControl>
                                            <RadioGroupItem value="rottamazione" className="mt-1 h-5 w-5 border-2 border-blue-300 text-blue-600 shadow-sm data-[state=checked]:border-blue-600 data-[state=checked]:text-blue-600 data-[state=checked]:bg-blue-50" />
                                        </FormControl>
                                        <div className="flex-1 !mt-0">
                                            <FormLabel className="font-semibold text-gray-900 !mt-0">Si, voglio rottamare il mio usato</FormLabel>
                                            <p className="text-sm text-gray-500">Bonus rottamazione 500EUR applicato al totale.</p>
                                        </div>
                                    </FormItem>
                                    <FormItem className={`flex items-start gap-4 px-6 py-5 transition ${field.value === "prenotazione" ? "bg-gray-50 rounded-b-2xl" : ""}`}>
                                        <FormControl>
                                            <RadioGroupItem value="prenotazione" className="mt-1 h-5 w-5 border-2 border-blue-200 text-blue-500 shadow-sm data-[state=checked]:border-blue-600 data-[state=checked]:text-blue-600 data-[state=checked]:bg-blue-50" />
                                        </FormControl>
                                        <div className="flex-1 !mt-0">
                                            <FormLabel className="font-semibold text-gray-900 !mt-0">No, preferisco non rottamare la mia auto</FormLabel>
                                            <p className="text-sm text-gray-500">Prosegui direttamente con la prenotazione.</p>
                                        </div>
                                    </FormItem>
                                </RadioGroup>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex w-full items-center justify-between">
                        <Button onClick={goBack} type="button" variant="ghost" className="text-primary hover:bg-transparent">Indietro</Button>
                        <Button className="sm:min-w-64 text-white bg-gradient-custom">Avanti</Button>
                    </div>
                </form>
            </Form>

            {isPermutaSelected && (
                <Dialog open={isPermutaSelected} onOpenChange={setPermutaSelected}>
                    <DialogTitle>Permuta il tuo usato</DialogTitle>
                    <DialogContent className="bg-white/90 min-w-[65%]">
                        <div>
                            <h1 className="text-2xl font-bold ">Permuta il tuo usato</h1>
                            <p className="text-gray-500 ">Compila tutti i campi </p>
                        </div>
                        <div>
                            <p>Ottieni <strong>il massimo valore</strong> dal tuo veicolo usato vendendo direttamente tramite la nostra <strong>piattaforma di aste</strong> al miglior offerente tra <strong>oltre 2000 commercianti</strong>, con una gestione completamente <strong>gratuita</strong>.</p>
                            <br />
                            <p>Dopo aver completato la prenotazione online, verrai contattato da un <strong>Advisor dedicato </strong> che ti assistera nell'organizzare un incontro per effettuare la <strong>perizia</strong> della tua auto.</p>
                        </div>
                        <Form {...permutaForm}>
                            <form onSubmit={permutaForm.handleSubmit(onSubmitPermuta)} className="space-y-4">
                                <Card className="mt-2 bg-transparent border-0 shadow-none">
                                    <CardHeader className="!py-2 !px-0">
                                        <CardTitle className="text-base">Dati usato</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4 !py-2 !px-0">
                                        <div className="grid grid-cols-2">
                                            <FormField control={permutaForm.control} name="targa" render={({ field, fieldState }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Controller control={permutaForm.control} name="targa" render={({ field }) => (
                                                            <Input {...field} maxLength={7} onInput={handleInputChange} placeholder="Targa" type="text" />
                                                        )} />
                                                    </FormControl>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )} />
                                            <FormField control={permutaForm.control} name="marca" render={({ fieldState }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Controller
                                                            control={permutaForm.control}
                                                            name="marca"
                                                            render={({ field }) => (
                                                                <Select {...field} onValueChange={(value) => {
                                                                    field.onChange(value);
                                                                    permutaForm.setValue("annoImmatricolazione", "");
                                                                    permutaForm.setValue("meseImmatricolazione", "");
                                                                    permutaForm.setValue("modello", "");
                                                                    permutaForm.setValue("versione", "");
                                                                }} value={field.value}>
                                                                    <SelectTrigger><SelectValue placeholder="Seleziona una marca" /></SelectTrigger>
                                                                    <SelectContent>{marche.map((marca, index) => <SelectItem key={index} value={marca.acronimo}>{marca.nome}</SelectItem>)}</SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </FormControl>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )} />
                                            <FormField control={permutaForm.control} name="annoImmatricolazione" render={({ fieldState }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Controller
                                                            control={permutaForm.control}
                                                            name="annoImmatricolazione"
                                                            render={({ field }) => (
                                                                <Select {...field} onValueChange={(value) => {
                                                                    field.onChange(value);
                                                                    permutaForm.setValue("modello", "");
                                                                    permutaForm.setValue("versione", "");
                                                                }} value={field.value} disabled={!permutaForm.watch("marca")}>
                                                                    <SelectTrigger><SelectValue placeholder="Seleziona un anno" /></SelectTrigger>
                                                                    <SelectContent>{anni.map((anno) => <SelectItem key={anno} value={anno}>{anno}</SelectItem>)}</SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </FormControl>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )} />
                                            <FormField control={permutaForm.control} name="meseImmatricolazione" render={({ fieldState }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Controller
                                                            control={permutaForm.control}
                                                            name="meseImmatricolazione"
                                                            render={({ field }) => (
                                                                <Select {...field} onValueChange={(value) => field.onChange(value)} value={field.value} disabled={!permutaForm.watch("annoImmatricolazione")}>
                                                                    <SelectTrigger><SelectValue placeholder="Seleziona un mese" /></SelectTrigger>
                                                                    <SelectContent>{mesi.map((mese, index) => <SelectItem key={index} value={mese.value}>{mese.label}</SelectItem>)}</SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </FormControl>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )} />
                                            <FormField control={permutaForm.control} name="modello" render={({ fieldState }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Controller
                                                            control={permutaForm.control}
                                                            name="modello"
                                                            render={({ field }) => (
                                                                <Select {...field} onValueChange={(value) => {
                                                                    field.onChange(value);
                                                                    permutaForm.setValue("versione", "");
                                                                }} value={field.value} disabled={!permutaForm.watch("meseImmatricolazione")}>
                                                                    <SelectTrigger><SelectValue placeholder="Seleziona un modello" /></SelectTrigger>
                                                                    <SelectContent>{modelli.map((modello, index) => <SelectItem key={index} value={modello.gammaModello.codice}>{modello.gammaModello.descrizione}</SelectItem>)}</SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </FormControl>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )} />
                                            <FormField control={permutaForm.control} name="versione" render={({ fieldState }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Controller
                                                            control={permutaForm.control}
                                                            name="versione"
                                                            render={({ field }) => (
                                                                <Select {...field} onValueChange={(value) => field.onChange(value)} value={field.value} disabled={!permutaForm.watch("modello")}>
                                                                    <SelectTrigger><SelectValue placeholder="Seleziona una versione" /></SelectTrigger>
                                                                    <SelectContent>{versioni.map((versione, index) => <SelectItem key={index} value={versione.codiceMotornetUnivoco}>{versione.nome}</SelectItem>)}</SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </FormControl>
                                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                                </FormItem>
                                            )} />
                                            <FormField control={permutaForm.control} name="km" render={({ field }) => (
                                                <FormItem><FormControl><Input placeholder="Km attuali" type="text" {...field} /></FormControl><FormMessage /></FormItem>
                                            )} />
                                            <FormField control={permutaForm.control} name="valoreDesiderato" render={({ field }) => (
                                                <FormItem><FormControl><Input placeholder="Valore di permuta desiderato" type="text" {...field} /></FormControl><FormMessage /></FormItem>
                                            )} />
                                        </div>
                                    </CardContent>
                                    <div className="grid grid-cols-4 gap-x-2 py-5">
                                        <PermutaService icon="/assets/icon/check-permuta.svg" title="Servizio di permuta gratuito" description="Nessuna commissione richiesta." />
                                        <PermutaService icon="/assets/icon/burocrazia.svg" title="Semplificazione delle pratiche burocratiche" description="Gestione tramite agenzie convenzionate." />
                                        <PermutaService icon="/assets/icon/trasparenza.svg" title="Massima trasparenza" description="Segui in tempo reale l'intero processo d'asta grazie a un link dedicato." />
                                        <PermutaService icon="/assets/icon/shild.svg" title="Garanzia sulla transazione economica" description="Nessun costo aggiuntivo o commissione percepita." />
                                    </div>
                                    <CardFooter className="mt-4">
                                        <div className="flex flex-col justify-center items-center w-full">
                                            <Button className="sm:min-w-64 text-white bg-gradient-custom " type="submit">Conferma</Button>
                                        </div>
                                    </CardFooter>
                                </Card>
                            </form>
                        </Form>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default PermutaLegacy;
